import { DateOnly, type MonthNumber, dateOnlyFormats } from "@anna-money/anna-web-lib/utils";

const DDMMYYYY_FORMAT = "DD/MM/YYYY";

// Parses our internal DD/MM/YYYY string into a timezone-agnostic DateOnly.
// Uses DateOnly.create (plain numbers) rather than DateOnly.parse with a format
// string so we don't depend on dayjs's customParseFormat plugin being loaded.
// Strict: rejects anything that isn't exactly DD/MM/YYYY and any impossible
// calendar date (e.g. 31/02) so it never silently rolls over into a wrong day.
export const parseDdmmyyyy = (date: string): DateOnly | null => {
  const parts = date.split("/");
  if (parts.length !== 3 || !parts.every((part) => /^\d+$/.test(part))) {
    return null;
  }
  const [day, month, year] = parts.map((part) => parseInt(part, 10));
  if (!day || !month || !year || month > 12 || day > 31) {
    return null;
  }
  const parsed = DateOnly.create(year, month as MonthNumber, day);
  if (parsed.day !== day || parsed.month !== month || parsed.year !== year) {
    return null;
  }
  return parsed;
};

export const formatDateYYYYMMDD = (dateString: string): string => {
  const date = parseDdmmyyyy(dateString);
  if (!date) {
    throw new Error("Invalid date format");
  }
  return date.format(dateOnlyFormats.ISO);
};

export const formatDateDDMMYYYY = (dateString: string): string => {
  // API returns dob as a date-only "YYYY-MM-DD" string. Slice to the first 10
  // chars so a full ISO datetime ("YYYY-MM-DDTHH:mm:ssZ") is handled too, and
  // parse via UTC-backed DateOnly so no local timezone is ever applied.
  const date = DateOnly.tryParse(dateString.slice(0, 10));
  if (!date) {
    throw new Error("Invalid date format");
  }
  return date.format(DDMMYYYY_FORMAT);
};
